import { useState, useEffect } from "react";

/* ---------- helpers ---------- */
function getWeekKey(date = new Date()) {
  const firstDay = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date - firstDay) / (24 * 60 * 60 * 1000));
  return `${date.getFullYear()}-W${Math.ceil((days + firstDay.getDay() + 1) / 7)}`;
}

function App() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const todayIndex = new Date().getDay();
  const todayAdjustedIndex = todayIndex === 0 ? 6 : todayIndex - 1;
  const weekKey = getWeekKey();

  const [activeTab, setActiveTab] = useState("today");
  const [dragId, setDragId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const [items, setItems] = useState(() => {
    const saved = JSON.parse(localStorage.getItem("itemsData"));
    if (!saved) return { weekKey, list: [] };

    if (saved.weekKey !== weekKey) {
      saved.list = saved.list.map((i) => ({
        ...i,
        weekly: Array(7).fill(false),
      }));
      saved.weekKey = weekKey;
    }

    return saved;
  });

  const [newItem, setNewItem] = useState("");
  const [selectedDays, setSelectedDays] = useState(Array(7).fill(false));

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDays, setEditDays] = useState(Array(7).fill(false));

  // Mark that the app has been used so returning users skip the landing page.
  useEffect(() => {
    localStorage.setItem("hasUsedApp", "true");
  }, []);

  useEffect(() => {
    localStorage.setItem("itemsData", JSON.stringify(items));
  }, [items]);

  /* ---------- actions ---------- */
  const addItem = (e) => {
    e.preventDefault();
    if (!newItem.trim() || !selectedDays.includes(true)) return;

    setItems((prev) => ({
      ...prev,
      list: [
        ...prev.list,
        {
          id: Date.now(),
          name: newItem.trim(),
          activeDays: selectedDays,
          weekly: Array(7).fill(false),
          archived: false,
        },
      ],
    }));

    setNewItem("");
    setSelectedDays(Array(7).fill(false));
  };

  const toggleDay = (id, index) => {
    setItems((prev) => ({
      ...prev,
      list: prev.list.map((i) =>
        i.id === id
          ? {
              ...i,
              weekly: i.weekly.map((d, idx) =>
                idx === index ? !d : d
              ),
            }
          : i
      ),
    }));
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditDays(item.activeDays);
  };

  const saveEdit = (id) => {
    if (!editName.trim() || !editDays.includes(true)) return;

    setItems((prev) => ({
      ...prev,
      list: prev.list.map((i) =>
        i.id === id
          ? { ...i, name: editName.trim(), activeDays: editDays }
          : i
      ),
    }));
    setEditingId(null);
  };

  const archiveItem = (id) => {
    setItems((prev) => ({
      ...prev,
      list: prev.list.map((i) =>
        i.id === id ? { ...i, archived: true } : i
      ),
    }));
  };

  const restoreItem = (id) => {
    setItems((prev) => ({
      ...prev,
      list: prev.list.map((i) =>
        i.id === id ? { ...i, archived: false } : i
      ),
    }));
  };

  const onDrop = (id) => {
    if (dragId === id) return;
    setItems((prev) => {
      const list = [...prev.list];
      const from = list.findIndex((i) => i.id === dragId);
      const to = list.findIndex((i) => i.id === id);
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...prev, list };
    });
  };

  /* ---------- stats ---------- */
  const todayItems = items.list.filter(
    (i) => !i.archived && i.activeDays[todayAdjustedIndex]
  );

  const todayDone = todayItems.filter(
    (i) => i.weekly[todayAdjustedIndex]
  ).length;

  const total = items.list.reduce(
    (a, i) => a + i.activeDays.filter(Boolean).length,
    0
  );

  const completed = items.list.reduce(
    (a, i) =>
      a + i.weekly.filter((d, idx) => d && i.activeDays[idx]).length,
    0
  );

  const progress = total ? Math.round((completed / total) * 100) : 0;

  const insightText =
    total === 0
      ? "Plan a few items to begin your week."
      : completed === total
      ? "You’ve shown up for everything this week."
      : todayDone === 0
      ? "One small action is enough for today."
      : "You’re moving at a steady pace.";

  const visibleItems = items.list.filter((i) =>
    showArchived ? true : !i.archived
  );

  return (
    <div className="min-h-screen bg-[#FAF7F5] px-4 py-8">
      <div className="mx-auto w-full max-w-md lg:max-w-6xl">
        {/* HEADER */}
        <div className="bg-white rounded-3xl shadow-sm p-8">
          <h1 className="text-3xl font-semibold text-[#7B4156] text-center">
            this week
          </h1>
          <p className="text-sm text-gray-500 text-center mt-1 mb-10">
            Small steps, every day.
          </p>

          {/* TABS */}
          <div className="mx-auto max-w-sm flex bg-[#EFE7EA] p-1 rounded-2xl mb-8">
            {["plan", "today", "overview"].map((t) => (
              <button
                key={t}
                onClick={() => setActiveTab(t)}
                className={`flex-1 py-1.5 rounded-xl text-sm transition
                  ${
                    activeTab === t
                      ? "bg-[#7B4156] text-white shadow-sm"
                      : "text-[#7B4156] opacity-70 hover:opacity-100"
                  }`}
              >
                {t === "plan" ? "Plan" : t === "today" ? "Today" : "Overview"}
              </button>
            ))}
          </div>

          {/* MAIN CONTENT */}
          <div className="lg:grid lg:grid-cols-[2fr_1fr] lg:gap-10">
            {/* LEFT */}
            <div>
              {activeTab === "today" && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500">
                    {todayDone}/{todayItems.length} completed today
                  </p>

                  {todayItems.map((i) => (
                    <div
                      key={i.id}
                      className={`p-4 rounded-2xl shadow-sm border-l-4
                        ${
                          i.weekly[todayAdjustedIndex]
                            ? "border-[#E6B8C5] bg-[#FAF7F5] opacity-60"
                            : "border-[#7B4156]"
                        }`}
                    >
                      <div className="flex justify-between">
                        <span
                          className={`${
                            i.weekly[todayAdjustedIndex]
                              ? "text-gray-400 line-through"
                              : "text-[#7B4156]"
                          }`}
                        >
                          {i.name}
                        </span>
                        <button
                          onClick={() =>
                            toggleDay(i.id, todayAdjustedIndex)
                          }
                          className={`
                            w-6 h-6 rounded-full border flex items-center justify-center
                            transition
                            ${
                              i.weekly[todayAdjustedIndex]
                                ? "bg-[#7B4156] border-[#7B4156]"
                                : "border-[#7B4156]/40 hover:bg-[#EFE7EA]"
                            }
                          `}
                        >
                          {i.weekly[todayAdjustedIndex] && (
                            <span className="text-white text-xs">✓</span>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "plan" && (
                <>
                  <form onSubmit={addItem} className="space-y-4 mb-6">
                    <input
                      value={newItem}
                      onChange={(e) => setNewItem(e.target.value)}
                      placeholder="Add item…"
                      className="w-full px-4 py-3 rounded-xl border border-[#7B4156]/30 focus:border-[#7B4156] text-[#7B4156] placeholder:text-gray-400"
                    />

                    <div className="grid grid-cols-7 gap-2">
                      {days.map((d, i) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() =>
                            setSelectedDays((p) =>
                              p.map((v, idx) => (idx === i ? !v : v))
                            )
                          }
                          className={`text-xs py-2 rounded-lg
                            ${
                              selectedDays[i]
                                ? "bg-[#7B4156] text-white"
                                : "bg-[#EFE7EA]/70 text-[#7B4156]/70"
                            }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>

                    <div className="flex justify-end">
                      <button
                        className="
                          px-5 py-2
                          rounded-xl
                          bg-[#7B4156]/85
                          text-white text-sm font-medium
                          hover:bg-[#7B4156]
                          transition
                        "
                      >
                        Add to week
                      </button>
                    </div>
                  </form>

                  <button
                    onClick={() => setShowArchived((v) => !v)}
                    className="text-xs text-gray-400 hover:text-[#7B4156] mb-4"
                  >
                    {showArchived ? "Hide archived" : "Show archived"}
                  </button>

                  <div className="space-y-4">
                    {visibleItems.map((i) => (
                      <div
                        key={i.id}
                        draggable
                        onDragStart={() => setDragId(i.id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => onDrop(i.id)}
                        className="bg-white/70 p-4 rounded-2xl shadow-sm cursor-move"
                      >
                        {editingId === i.id ? (
                          <>
                            <input
                              value={editName}
                              onChange={(e) =>
                                setEditName(e.target.value)
                              }
                              className="w-full mb-3 px-3 py-2 rounded-lg border"
                            />

                            <div className="grid grid-cols-7 gap-1 mb-3">
                              {days.map((day, idx) => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() =>
                                    setEditDays((prev) =>
                                      prev.map((d, i2) =>
                                        i2 === idx ? !d : d
                                      )
                                    )
                                  }
                                  className={`text-[10px] py-1 rounded
                                    ${
                                      editDays[idx]
                                        ? "bg-[#7B4156] text-white"
                                        : "bg-[#EFE7EA] text-[#7B4156]"
                                    }`}
                                >
                                  {day}
                                </button>
                              ))}
                            </div>

                            <div className="flex justify-end gap-3">
                              <button
                                onClick={() => setEditingId(null)}
                                className="text-xs text-gray-400"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => saveEdit(i.id)}
                                className="text-xs text-[#7B4156]"
                              >
                                Save
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between">
                            <span className="text-[#7B4156] font-medium">
                              {i.name}
                            </span>
                            <div className="flex gap-3 text-xs">
                              {!i.archived && (
                                <button
                                  onClick={() => startEdit(i)}
                                  className="text-xs text-gray-400 hover:text-[#7B4156]"
                                >
                                  Edit
                                </button>
                              )}
                              {!i.archived ? (
                                <button
                                  onClick={() =>
                                    archiveItem(i.id)
                                  }
                                  className="text-xs text-gray-400 hover:text-[#7B4156]"
                                >
                                  Archive
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    restoreItem(i.id)
                                  }
                                  className="text-xs text-gray-400 hover:text-[#7B4156]"
                                >
                                  Restore
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {activeTab === "overview" && (
                <div className="text-center space-y-3">
                  <p className="text-gray-600">Weekly progress</p>
                  <p className="text-4xl font-semibold text-[#7B4156]">
                    {progress}%
                  </p>
                </div>
              )}
            </div>

            {/* RIGHT – DESKTOP PANEL */}
<div className="hidden lg:block">
  <div className="bg-[#FAF7F5] rounded-2xl p-6 space-y-5">
    {/* TODAY CONTEXT */}
    <div>
      <p className="text-sm text-gray-500">Today</p>
      <p className="text-2xl font-semibold text-[#7B4156]">
        {todayDone}/{todayItems.length}
      </p>
      <p className="text-xs text-gray-500">
        completed so far
      </p>
    </div>

    {/* INSIGHT */}
    <div className="pt-4 border-t">
      <p className="text-xs text-gray-500 mb-1">
        This week
      </p>
      <p className="text-sm text-[#7B4156] leading-relaxed">
        {insightText}
      </p>
      {/* Removed closing quote text */}
    </div>
  </div>
</div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
